/**
 * Утилиты для работы с обработчиками событий
 * Упрощает регистрацию и отмену подписок
 */

/**
 * Тип функции-обработчика события
 */
export type EventHandler<T = any> = (payload: T) => void;

/**
 * Тип функции отмены подписки
 */
export type UnsubscribeFn = () => void;

/**
 * Менеджер обработчиков событий
 * Упрощает регистрацию, вызов и отмену подписок на события
 */
export class EventHandlerManager<TEventMap extends Record<string, any>> {
  /** Хранилище обработчиков по типам событий */
  private handlers: Map<keyof TEventMap, EventHandler[]> = new Map();

  /**
   * Регистрирует обработчик для указанного типа события
   * @param eventType - Тип события
   * @param handler - Функция-обработчик
   * @returns Функция для отмены подписки
   */
  on<K extends keyof TEventMap>(
    eventType: K,
    handler: EventHandler<TEventMap[K]>
  ): UnsubscribeFn {
    // Получаем или создаем массив обработчиков для данного типа события
    if (!this.handlers.has(eventType)) {
      this.handlers.set(eventType, []);
    }

    const eventHandlers = this.handlers.get(eventType)!;
    eventHandlers.push(handler);

    // Возвращаем функцию для отмены подписки
    return () => {
      const index = eventHandlers.indexOf(handler);
      if (index !== -1) {
        eventHandlers.splice(index, 1);
      }
    };
  }

  /**
   * Вызывает все обработчики для указанного типа события
   * @param eventType - Тип события
   * @param payload - Данные события
   */
  emit<K extends keyof TEventMap>(eventType: K, payload: TEventMap[K]): void {
    const eventHandlers = this.handlers.get(eventType);

    if (eventHandlers && eventHandlers.length > 0) {
      // Вызываем каждый обработчик с переданными данными
      eventHandlers.forEach(handler => {
        try {
          handler(payload);
        } catch (error) {
          console.error(`Error in ${String(eventType)} handler:`, error);
        }
      });
    }
  }

  /**
   * Удаляет все обработчики для указанного типа события
   * @param eventType - Тип события
   */
  clear(eventType: keyof TEventMap): void {
    this.handlers.delete(eventType);
  }

  /**
   * Удаляет все обработчики для всех событий
   */
  clearAll(): void {
    this.handlers.clear();
  }

  /**
   * Проверяет наличие обработчиков для указанного типа события
   * @param eventType - Тип события
   * @returns true если есть хотя бы один обработчик
   */
  has(eventType: keyof TEventMap): boolean {
    const eventHandlers = this.handlers.get(eventType);
    return !!eventHandlers && eventHandlers.length > 0;
  }

  /**
   * Возвращает количество обработчиков для указанного типа события
   * @param eventType - Тип события
   * @returns Количество обработчиков
   */
  count(eventType: keyof TEventMap): number {
    const eventHandlers = this.handlers.get(eventType);
    return eventHandlers ? eventHandlers.length : 0;
  }
}

/**
 * Вычисляет задержку для повторной попытки с экспоненциальным backoff
 * @param attempt - Номер попытки (начинается с 1)
 * @param baseDelay - Базовая задержка в мс
 * @param maxDelay - Максимальная задержка в мс
 * @returns Задержка в миллисекундах
 */
export function calculateExponentialBackoff(
  attempt: number,
  baseDelay: number = 1000,
  maxDelay: number = 30000
): number {
  const delay = baseDelay * Math.pow(2, attempt - 1);
  return Math.min(delay, maxDelay);
}
